import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import {
  calculatePlan,
  calculateNutritionPreview,
  isOutOfStockError,
  CalculationPlanResponse,
  CalculationPreview,
} from '../../services/api';
import { buildApiPayload } from '../../utils/formPayload';
import { getTransitionSchedule, pickCurrentFoodType, deriveDigestiveModifiers } from '../../utils/transitionSchedule';
import { resolveFunnelMode } from '../funnelMode';
import { AgeCategory, DogFormData } from '../../types';

interface ResultKcalProps {
  planCalculate?: (formData: Partial<DogFormData>, signal?: AbortSignal) => Promise<CalculationPlanResponse>;
  previewCalculate?: (formData: Partial<DogFormData>, signal?: AbortSignal) => Promise<CalculationPreview>;
}

// Emoji por tipo de proteína para dar un toque visual a cada sabor.
const PROTEIN_EMOJI: Record<string, string> = {
  pollo: '🍗',
  pavo: '🦃',
  ternera: '🥩',
  buey: '🥩',
  cerdo: '🐷',
  cordero: '🐑',
  pato: '🦆',
  conejo: '🐰',
  salmon: '🐟',
  salmón: '🐟',
  pescado: '🐟',
};
const emojiFor = (protein?: string): string => PROTEIN_EMOJI[(protein || '').toLowerCase()] || '🍽️';

const capitalizeWord = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Etiqueta corta del sabor principal a partir de la(s) proteína(s):
// ["pollo"] → "Pollo"; ["pollo","ternera"] → "Pollo y Ternera".
const joinFlavors = (proteins: string[]): string => {
  const parts = proteins.map((p) => capitalizeWord((p || '').trim())).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
};
// Etapa de vida legible: adult→Adulto, puppy/cachorro→Cachorro, senior→Senior.
const STAGE_LABEL: Record<string, string> = {
  adult: 'Adulto',
  adulto: 'Adulto',
  puppy: 'Cachorro',
  cachorro: 'Cachorro',
  senior: 'Senior',
};
const stageLabel = (stage?: string | null) => STAGE_LABEL[(stage || '').toLowerCase()] ?? '';

// Como texto visible mostramos la etiqueta corta de sabor ("Pollo Adulto"); el
// nombre comercial completo queda accesible en title/aria del elemento.

// Miniatura reutilizable: imagen propia del producto y, si falta o falla la carga
// (p.ej. 404 de Shopify), cae al emoji de la proteína. alt="" y emoji aria-hidden
// porque el nombre accesible lo aporta el contenedor (aria-label o el texto al lado).
const Thumbnail: React.FC<{
  src?: string;
  protein?: string;
  className?: string;
  emojiClassName?: string;
}> = ({ src, protein, className = '', emojiClassName = 'text-xl' }) => {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return (
      <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} className={`object-contain ${className}`} />
    );
  }
  return (
    <span aria-hidden className={`flex items-center justify-center ${className}`}>
      <span className={emojiClassName}>{emojiFor(protein)}</span>
    </span>
  );
};

// `calculatePlan` (services/api) llama a POST /calculate-plan y no acepta
// AbortSignal (no soporta cancelación de red); este adaptador lo ajusta a la
// forma que espera `planCalculate` para poder inyectar un mock en tests.
const defaultPlanCalculate = (formData: Partial<DogFormData>): Promise<CalculationPlanResponse> =>
  calculatePlan(formData as DogFormData);

// La calculadora es INFORMATIVA (no vende). Cuando /calculate-plan falla por
// causas de venta (stock/precio/hueco de catálogo), caemos a /calculate-preview
// para dar al menos las kcal/día — el dato principal de esta pantalla.
type CalcStatus = 'loading' | 'full' | 'kcal-only' | 'error';

// Cuenco de comida natural — activo de marca reutilizado como "medallón" del
// resultado: la ración es, literalmente, lo que va HOY en su cuenco. Cae con
// gracia (fondo blanco del medallón = fondo blanco del PNG) si la imagen falta.
const BOWL_IMG = '/images/funnel/bowl-natural.png';

// Micro-animaciones (flotar del cuenco + revelado suave del resultado). Se
// definen una vez y se desactivan con prefers-reduced-motion. Prefijo `kcal-`
// para no colisionar con las de otras pantallas (p.ej. dogFloat en P9).
const KEYFRAMES = `
@keyframes kcalBowlFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
@keyframes kcalFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.kcal-bowl { animation: kcalBowlFloat 3.5s ease-in-out infinite; }
.kcal-reveal { animation: kcalFadeUp .5s cubic-bezier(.2,.7,.3,1) both; }
@media (prefers-reduced-motion: reduce) { .kcal-bowl, .kcal-reveal { animation: none; } }
`;

const ResultKcal: React.FC<ResultKcalProps> = ({
  planCalculate = defaultPlanCalculate,
  previewCalculate = calculateNutritionPreview,
}) => {
  const { formData, goToPreviousStep } = useForm();
  const { config, t } = useAppConfig();
  const [plan, setPlan] = useState<CalculationPlanResponse | null>(null);
  const [fallbackKcal, setFallbackKcal] = useState<number | null>(null);
  const [status, setStatus] = useState<CalcStatus>('loading');
  // Mensaje del backend a mostrar en el estado de error (p.ej. aviso clínico).
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Sabores que el dueño va a dar HOY (subconjunto de los elegidos). null = todos.
  const [todayIds, setTodayIds] = useState<Set<string> | null>(null);
  // Móvil: la transición se puede plegar (la ración es lo principal). En PC (md+)
  // ración y transición van lado a lado y ambas se muestran siempre.
  const [showTransition, setShowTransition] = useState(true);

  const runCalc = useCallback(
    (signal?: AbortSignal) => {
      setStatus('loading');
      setErrorMsg(null);
      const payload = buildApiPayload(formData, { informative: true, platform: config.effective_platform });
      return planCalculate(payload, signal)
        .then((res) => {
          if (signal?.aborted) return;
          // Defensa ante un 2xx malformado: sin array de recetas no hay plan
          // que mostrar (evita quedarse en spinner con status 'full' y !plan).
          if (!res || !Array.isArray(res.recipes)) {
            setStatus('error');
            return;
          }
          setPlan(res);
          setStatus('full');
        })
        .catch(async (err) => {
          // Ignora la cancelación por desmontaje (React StrictMode monta dos
          // veces en dev y aborta la 1ª petición): no es un error real.
          if (signal?.aborted) return;
          // Solo caemos al preview informativo ante fallos de VENTA (stock): la
          // calculadora no debe romperse porque un sabor esté agotado. Cualquier
          // OTRO error (p.ej. 400 clínico "requiere valoración veterinaria", o
          // "no hay recetas compatibles") se MUESTRA tal cual — nunca lo
          // ocultamos tras unas kcal aparentemente normales del preview, que no
          // aplica esas exclusiones clínicas.
          if (!isOutOfStockError(err)) {
            setErrorMsg(err instanceof Error ? err.message : null);
            setStatus('error');
            return;
          }
          try {
            const preview = await previewCalculate(payload, signal);
            if (signal?.aborted) return;
            if (!Number.isFinite(preview?.daily_kcal)) {
              setStatus('error');
              return;
            }
            setFallbackKcal(Math.round(preview.daily_kcal));
            setStatus('kcal-only');
          } catch {
            if (signal?.aborted) return;
            setStatus('error');
          }
        });
    },
    [formData, planCalculate, previewCalculate, config.effective_platform],
  );

  useEffect(() => {
    const controller = new AbortController();
    runCalc(controller.signal);
    return () => controller.abort();
    // Solo al montar: el retry manual reinvoca runCalc con un signal nuevo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schedule = useMemo(() => {
    const baseFood = pickCurrentFoodType(formData.currentFood);
    const digestive = deriveDigestiveModifiers(formData.pathologies, formData.subPathologies);
    return getTransitionSchedule(baseFood, {
      isCachorro: formData.ageCategory === AgeCategory.CACHORRO,
      isSenior: formData.ageCategory === AgeCategory.SENIOR,
      ...digestive,
    });
  }, [formData.currentFood, formData.ageCategory, formData.pathologies, formData.subPathologies]);

  const nombre = formData.nombre || 'Tu perro';
  // Solo ofrecemos "Ver menú completo" cuando la compra está realmente accesible
  // para ESTE visitante: si llegó con `?checkout=0` (informativo explícito) no le
  // tentamos con el flujo de compra. Reutiliza la misma lógica del seam del funnel
  // ("la URL manda" sobre el flag global). Se preserva el query string al saltar
  // a la raíz para no perder la intención de compra (`?checkout=1`).
  const funnelSearch = typeof window !== 'undefined' ? window.location.search : '';
  const canEnterFullFunnel =
    resolveFunnelMode('/', config.runtime_controls?.full_funnel_enabled, funnelSearch) === 'full';

  // Miniatura de cada receta = la imagen PROPIA del producto (Product.image_url),
  // tomada de config.products por product.id (UUID) — cubre cada variante (400g/800g…),
  // así que casa siempre con las recetas del plan aunque la config agrupe sabores.
  const imgFor = useMemo(() => {
    const map = new Map<string, string | undefined>();
    (config.products ?? []).forEach((p) => map.set(String(p.id), p.image_url ?? undefined));
    return (productId: string) => map.get(String(productId));
  }, [config.products]);

  // Sabor principal + etapa por variante ("Pollo Adulto", "Pollo y Ternera Cachorro"):
  // usa protein_types (multi) si existe, si no protein_type, y life_stage; con
  // fallback a la proteína y la etapa de la propia receta/plan.
  const flavorFor = useMemo(() => {
    const map = new Map<string, { proteins: string[]; stage?: string | null }>();
    (config.products ?? []).forEach((p) => {
      const proteins = p.protein_types?.length ? p.protein_types : [p.protein_type];
      map.set(String(p.id), { proteins: proteins.filter(Boolean), stage: p.life_stage });
    });
    return (productId: string, fallbackProtein?: string, fallbackStage?: string) => {
      const entry = map.get(String(productId));
      const proteins = entry?.proteins.length ? entry.proteins : fallbackProtein ? [fallbackProtein] : [];
      const flavor = joinFlavors(proteins);
      const stage = stageLabel(entry?.stage ?? fallbackStage);
      return [flavor, stage].filter(Boolean).join(' ');
    };
  }, [config.products]);

  const toggleToday = (id: string) => {
    setTodayIds((prev) => {
      const base = new Set(prev ?? (plan ? plan.recipes.map((r) => r.recipe_id) : []));
      if (base.has(id)) base.delete(id);
      else base.add(id);
      return base;
    });
  };

  const retryButton = (label: string) => (
    <button
      type="button"
      onClick={() => runCalc()}
      className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#008BBC] px-5 py-2.5 font-fredoka text-sm font-semibold text-white shadow-md transition hover:bg-[#007aa8]"
    >
      {label}
    </button>
  );

  const body = () => {
    if (status === 'error')
      return (
        <div role="alert" className="flex flex-col items-center gap-1 py-8 text-center">
          <span aria-hidden className="mb-1 text-3xl">
            🐾
          </span>
          <p className="font-poppins text-sm text-gray-700">{errorMsg || 'No hemos podido calcular las calorías.'}</p>
          {retryButton('Reintentar')}
        </div>
      );
    if (status === 'kcal-only' && fallbackKcal !== null)
      // Un sabor quedó sin stock, pero las kcal sí se pueden dar: mostramos el
      // dato principal (vía /calculate-preview) y ofrecemos recalcular los
      // gramos por sabor. OJO: preview y plan no siempre coinciden en el DER
      // (p.ej. cachorros con historial de crecimiento no reflejado en el
      // preview), por eso lo presentamos explícitamente como ESTIMACIÓN.
      return (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <span className="kcal-bowl flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_12px_26px_-8px_rgba(0,80,120,0.3)] ring-1 ring-[#90D1E8]/50">
            <img src={BOWL_IMG} alt="" className="h-full w-full object-contain p-0.5" />
          </span>
          <div>
            <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.18em] text-[#008BBC]/70">
              {nombre} necesita al día
            </p>
            <p className="font-fredoka text-4xl font-semibold text-[#008BBC]">≈ {fallbackKcal} kcal/día</p>
            <p className="font-poppins text-xs text-gray-400">estimación aproximada</p>
          </div>
          <p className="max-w-sm font-poppins text-sm text-gray-500">
            No hemos podido calcular ahora mismo los gramos de cada sabor.
          </p>
          {retryButton('Reintentar el cálculo completo')}
        </div>
      );
    if (status === 'loading' || !plan)
      return (
        <div aria-live="polite" className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="kcal-bowl flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_10px_22px_-8px_rgba(0,80,120,0.3)] ring-1 ring-[#90D1E8]/50">
            <img src={BOWL_IMG} alt="" className="h-full w-full object-contain p-0.5" />
          </span>
          <p className="font-poppins text-sm text-gray-500">Calculando la ración de {nombre}…</p>
        </div>
      );

    const dailyKcal = Math.round(plan.der);
    const numRecipes = plan.recipes.length;
    // El funnel kcal es dieta 'completa' por diseño (no pregunta tipo de dieta;
    // el payload cae a COMPLETA). Solo un draft hidratado como 'mixta' podría
    // llegar aquí con otra cosa → ajustamos el copy para no mentir.
    const isCompleta = (plan.diet_type ?? 'completa') === 'completa';
    // Ración COMPLETA de un solo sabor. Asume dieta 'completa' (todo el DER en
    // MyL) y reparto equitativo del DER por kcal entre las recetas del plan
    // (daily_grams * N reconstruye la ración completa). En 'mixta' daily_grams es
    // solo la fracción de MyL, así que * N NO da el DER total; un reparto
    // ponderado (no equitativo) también rompería esta reconstrucción.
    const fullGramsOf = (r: (typeof plan.recipes)[number]) => Math.round(r.daily_grams * numRecipes);
    // Gramos/día del titular: media de la ración completa (un solo sabor) entre los
    // sabores elegidos — con densidades distintas cada sabor da gramos algo distintos.
    const avgDailyGrams =
      numRecipes > 0 ? Math.round(plan.recipes.reduce((s, r) => s + fullGramsOf(r), 0) / numRecipes) : 0;

    // Sabores marcados para HOY (por defecto, todos los elegidos).
    const activeIds = todayIds ?? new Set(plan.recipes.map((r) => r.recipe_id));
    const selected = plan.recipes.filter((r) => activeIds.has(r.recipe_id));
    const m = selected.length;
    // El DER del día se reparte entre los sabores marcados hoy: con 1 sabor, ese
    // sabor recibe la ración completa; con 2, la mitad de kcal cada uno (los
    // gramos difieren por densidad). No se suma un total salvo que haya mezcla.
    const rationOf = (r: (typeof plan.recipes)[number]) => (m > 0 ? Math.round(fullGramsOf(r) / m) : 0);
    const totalRation = selected.reduce((sum, r) => sum + rationOf(r), 0);

    // Tramos de transición (agrupa días consecutivos con el mismo % de MyL). La
    // ración completa (100%) va en la fila final de la tabla, no como un tramo más.
    const groups: Array<{ startDay: number; endDay: number; pctMyL: number }> = [];
    schedule.forEach((d) => {
      const last = groups[groups.length - 1];
      if (last && last.pctMyL === d.pctMyL) last.endDay = d.day;
      else groups.push({ startDay: d.day, endDay: d.day, pctMyL: d.pctMyL });
    });
    const transitionDays = schedule.length;
    const rampGroups = groups.filter((g) => g.pctMyL < 100);
    const firstFullDay = schedule.find((d) => d.pctMyL === 100)?.day ?? schedule.length + 1;
    const rangeLabel = (g: { startDay: number; endDay: number }) =>
      g.startDay === g.endDay ? `Día ${g.startDay}` : `Días ${g.startDay}-${g.endDay}`;

    // Ancho de la barra de proporción de cada sabor, relativo al mayor del día.
    const maxRation = m > 0 ? Math.max(...selected.map((r) => rationOf(r))) : 0;
    // Timeline de transición: cada tramo del schedule + la ración completa (100%)
    // como hito final. El orden ES información aquí (la ración sube "poco a poco"),
    // así que una secuencia por etapas es la forma honesta y legible de mostrarlo.
    const stages: Array<{ key: string; label: string; pct: number; full: boolean }> = [
      ...rampGroups.map((g) => ({ key: `g${g.startDay}`, label: rangeLabel(g), pct: g.pctMyL, full: false })),
      { key: 'full', label: `Día ${firstFullDay}+`, pct: 100, full: true },
    ];

    return (
      <div className="kcal-reveal flex flex-col">
        {/* HERO — el cuenco de HOY: la ración es, literalmente, lo que va en su
            plato. El fondo azul suave sangra a los bordes de la card (cancela el
            padding p-5/p-6 del FunnelLayout) para leerse como una franja de marca. */}
        <header
          className="relative -mx-5 -mt-5 rounded-t-[20px] px-5 pb-6 pt-7 text-center sm:-mx-6 sm:-mt-6 sm:px-6"
          style={{
            background: 'radial-gradient(120% 90% at 50% -10%, rgba(144,209,232,0.35) 0%, rgba(247,244,238,0) 60%)',
          }}
        >
          <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.18em] text-[#008BBC]/70">
            {t('resultkcal.daily_need', '{nombre} necesita al día', { nombre })}
          </p>
          <div className="mt-3 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-7">
            <span className="kcal-bowl flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_12px_26px_-8px_rgba(0,80,120,0.3)] ring-1 ring-[#90D1E8]/50 sm:h-32 sm:w-32">
              <img src={BOWL_IMG} alt="" className="h-full w-full object-contain p-0.5" />
            </span>
            <div className="text-center sm:text-left">
              <p className="font-fredoka text-[2.75rem] font-semibold leading-none text-[#008BBC] sm:text-6xl">
                ≈ {avgDailyGrams} g/día
              </p>
              <div className="mt-3 flex items-center justify-center gap-2 sm:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F6A04D]/15 px-3 py-1 font-poppins text-sm font-semibold text-[#b26a1f]">
                  <span aria-hidden>🔥</span> ≈ {dailyKcal} kcal/día
                </span>
              </div>
              {numRecipes > 1 && (
                <p className="mt-2 font-poppins text-xs text-gray-400">
                  {t('resultkcal.avg_note', 'de media entre {n} sabores', { n: numRecipes })}
                </p>
              )}
            </div>
          </div>
        </header>

        <div className="flex flex-col gap-6 pt-6">
          {/* Selector: qué sabores le da HOY */}
          <section>
            <h2 className="font-fredoka text-base font-semibold text-gray-900">
              {t('resultkcal.today_title', '¿Qué le quieres dar hoy?')}
            </h2>
            <p className="mb-3 font-poppins text-xs text-gray-500">
              {t('resultkcal.today_help', 'Toca los sabores del día para ver sus gramos.')}
            </p>
            <div className="flex flex-wrap gap-3">
              {plan.recipes.map((r) => {
                const on = activeIds.has(r.recipe_id);
                const img = imgFor(r.recipe_id);
                return (
                  <button
                    key={r.recipe_id}
                    type="button"
                    aria-pressed={on}
                    aria-label={r.recipe_name}
                    title={r.recipe_name}
                    onClick={() => toggleToday(r.recipe_id)}
                    className={`relative flex w-24 flex-col items-center gap-1.5 rounded-2xl border p-2.5 text-center transition hover:-translate-y-0.5 ${
                      on
                        ? 'border-[#008BBC]/70 bg-[#008BBC]/5 ring-2 ring-[#008BBC]/15'
                        : 'border-gray-200 bg-white opacity-60 hover:opacity-100'
                    }`}
                  >
                    {on && (
                      <span
                        aria-hidden
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#008BBC] text-[11px] font-bold text-white shadow-sm"
                      >
                        ✓
                      </span>
                    )}
                    <Thumbnail src={img} protein={r.protein_type} className="h-14 w-14" emojiClassName="text-3xl" />
                    <span className="font-poppins text-[11px] font-semibold leading-tight text-gray-700">
                      {flavorFor(r.recipe_id, r.protein_type, plan.life_stage)}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {m === 0 ? (
            <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
              {t('resultkcal.pick_one', 'Elige al menos un sabor para ver los gramos.')}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 md:items-stretch">
              {/* 1) Ración diaria — lo principal: arriba en móvil, izquierda en PC */}
              <section className="flex min-w-0 flex-col rounded-2xl bg-[#008BBC]/[0.05] p-5 ring-1 ring-[#008BBC]/10">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-[#008BBC]/10 text-base"
                  >
                    🍽️
                  </span>
                  <h2 className="font-fredoka text-base font-semibold text-[#008BBC] sm:text-lg">
                    {t('resultkcal.ration_title', 'Su ración diaria')}
                  </h2>
                </div>
                <p className="mb-4 mt-0.5 font-poppins text-xs text-gray-500">
                  {isCompleta
                    ? m === 1
                      ? t(
                          'resultkcal.ration_help_single',
                          'Dándole solo este sabor, esta es su ración completa del día:',
                        )
                      : t('resultkcal.ration_help_multi', 'Repartida entre los sabores de hoy:')
                    : t(
                        'resultkcal.ration_help_mixed',
                        'Gramos de Milo y Lola (en dieta mixta, el resto del plato es su comida actual):',
                      )}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {selected.map((r) => (
                    <li
                      key={r.recipe_id}
                      className="rounded-xl bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Thumbnail
                            src={imgFor(r.recipe_id)}
                            protein={r.protein_type}
                            className="h-9 w-9 shrink-0"
                            emojiClassName="text-lg"
                          />
                          <span className="min-w-0 font-poppins text-sm text-gray-800" title={r.recipe_name}>
                            {flavorFor(r.recipe_id, r.protein_type, plan.life_stage)}
                          </span>
                        </span>
                        <span className="shrink-0 whitespace-nowrap font-fredoka text-base font-semibold text-[#008BBC]">
                          {rationOf(r)} g/día
                        </span>
                      </div>
                      {maxRation > 0 && (
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#008BBC]/10">
                          <div
                            className="h-full rounded-full bg-[#008BBC]/60"
                            style={{ width: `${Math.round((rationOf(r) / maxRation) * 100)}%` }}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {m > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t border-[#008BBC]/15 pt-4 md:mt-auto">
                    <span className="font-fredoka text-sm font-semibold text-gray-700">
                      {t('resultkcal.total', 'Total del día')}
                    </span>
                    <span className="font-fredoka text-lg font-semibold text-[#008BBC]">{totalRation} g</span>
                  </div>
                )}
              </section>

              {/* 2) Periodo de transición — debajo en móvil (plegable), a la derecha en PC */}
              <div className="flex min-w-0 flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setShowTransition((v) => !v)}
                  aria-expanded={showTransition}
                  className="flex items-center justify-between gap-2 rounded-xl bg-[#F6A04D]/10 px-4 py-2.5 text-left font-fredoka text-sm font-semibold text-[#c07321] ring-1 ring-[#F6A04D]/25 md:hidden"
                >
                  <span className="flex items-center gap-2">
                    <span aria-hidden>🔄</span>
                    {t('resultkcal.transition_title', 'Periodo de transición ({dias} días)', { dias: transitionDays })}
                  </span>
                  <span aria-hidden>{showTransition ? '▲' : '▼'}</span>
                </button>
                <section
                  className={`${showTransition ? 'flex' : 'hidden'} flex-col rounded-2xl bg-[#F6A04D]/[0.07] p-5 ring-1 ring-[#F6A04D]/20 md:flex md:flex-1`}
                >
                  <div className="mb-1 hidden items-center gap-2 md:flex">
                    <span
                      aria-hidden
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F6A04D]/15 text-base"
                    >
                      🔄
                    </span>
                    <h2 className="font-fredoka text-base font-semibold text-[#c07321] sm:text-lg">
                      {t('resultkcal.transition_title', 'Periodo de transición ({dias} días)', {
                        dias: transitionDays,
                      })}
                    </h2>
                  </div>
                  {rampGroups.length > 0 ? (
                    <>
                      <p className="mb-4 mt-0.5 font-poppins text-xs text-[#9a6a34]">
                        {t(
                          'resultkcal.transition_help',
                          'Cámbiala poco a poco. Gramos de Milo y Lola por sabor (el resto del plato, su comida actual):',
                        )}
                      </p>
                      <ol className="relative mt-1 flex-1">
                        {stages.map((s, i) => (
                          <li key={s.key} className="relative pb-4 pl-8 last:pb-0">
                            {i < stages.length - 1 && (
                              <span
                                aria-hidden
                                className="absolute left-[9px] top-5 h-[calc(100%-0.5rem)] w-0.5 -translate-x-1/2 bg-[#F6A04D]/25"
                              />
                            )}
                            {s.full ? (
                              <span
                                aria-hidden
                                className="absolute left-0 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#4CAF50] text-[10px] font-bold text-white ring-4 ring-[#4CAF50]/15"
                              >
                                ✓
                              </span>
                            ) : (
                              <span
                                aria-hidden
                                className="absolute left-[3px] top-1 h-3.5 w-3.5 rounded-full bg-[#F6A04D] ring-4 ring-[#F6A04D]/15"
                              />
                            )}
                            <div className="flex items-baseline gap-2">
                              <span
                                className={`font-fredoka text-sm font-semibold ${s.full ? 'text-[#4CAF50]' : 'text-gray-800'}`}
                              >
                                {s.label}
                              </span>
                              <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                  s.full ? 'bg-[#4CAF50]/15 text-[#4CAF50]' : 'bg-[#F6A04D]/15 text-[#b26a1f]'
                                }`}
                              >
                                {s.full ? 'ración completa' : `${s.pct}%`}
                              </span>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-poppins text-[13px] text-gray-700">
                              {selected.map((r, idx) => (
                                <React.Fragment key={r.recipe_id}>
                                  {idx > 0 && (
                                    <span aria-hidden className="text-gray-300">
                                      ·
                                    </span>
                                  )}
                                  <span title={r.recipe_name}>
                                    {flavorFor(r.recipe_id, r.protein_type, plan.life_stage)}{' '}
                                    <b className={`font-semibold ${s.full ? 'text-[#4CAF50]' : 'text-gray-900'}`}>
                                      {Math.round((rationOf(r) * s.pct) / 100)} g
                                    </b>
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : (
                    <p className="font-poppins text-sm text-[#9a6a34]">
                      {t('resultkcal.transition_none', 'Puedes darle su ración completa desde el primer día.')}
                    </p>
                  )}
                </section>
              </div>
            </div>
          )}

          {canEnterFullFunnel && (
            <div className="flex justify-center pt-1">
              <a
                href={`/${funnelSearch}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#008BBC] px-6 py-3 font-fredoka text-base font-semibold text-white shadow-md transition hover:bg-[#007aa8]"
              >
                Ver menú completo <span aria-hidden>→</span>
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <FunnelLayout step={9} useTwoPhase maxWidth="4xl" showContinue={false} onBack={goToPreviousStep}>
      <style>{KEYFRAMES}</style>
      {body()}
    </FunnelLayout>
  );
};

export default ResultKcal;
